export interface UserSession {
  id: number;
  username: string;
  full_name: string;
  status: string;
  is_superuser: boolean;
  platform_roles: string[];
  must_change_password: boolean;
  must_complete_profile?: boolean;
  missing_required_profile_fields?: string[];
  expires_at?: string | null;
  impersonation?: {
    actor_user_id: number;
    actor_username: string;
    actor_full_name: string;
    actor_role?: string | null;
    actor_platform_roles?: string[];
  } | null;
  avatar_url?: string | null;
  phone?: string | null;
  created_at: string;
  updated_at: string;
}

export const ENVIRONMENT_PLATFORM_ROLES = ["platform_admin", "environment_admin", "environment_designer"] as const;
export const IDENTITY_PLATFORM_ROLES = ["platform_admin", "identity_admin"] as const;
export const ASSET_PLATFORM_ROLES = ["platform_admin", "asset_admin", "asset_editor"] as const;
export const EXECUTION_PLATFORM_ROLES = ["platform_admin", "execution_admin", "platform_operator"] as const;
export const INTELLIGENCE_PLATFORM_ROLES = ["platform_admin", "intelligence_admin"] as const;
export const TACTICS_TECHNIQUES_PLATFORM_ROLES = ["platform_admin", "tactics_techniques_admin"] as const;
export const CAPABILITY_GRAPH_PLATFORM_ROLES = ["platform_admin", "capability_graph_admin"] as const;
export const VULNERABILITY_LIBRARY_PLATFORM_ROLES = ["platform_admin", "vulnerability_library_admin"] as const;
export const BUSINESS_REFERENCE_PLATFORM_ROLES = ["platform_admin", "business_reference_admin"] as const;
export const TRAFFIC_LIBRARY_PLATFORM_ROLES = ["platform_admin", "traffic_library_admin"] as const;
export const AIRANGE_ADMIN_PLATFORM_ROLES = [
  "platform_admin",
  "identity_admin",
  "asset_admin",
  "asset_editor",
  "execution_admin",
  "environment_admin",
  "environment_designer",
  "capability_graph_admin",
  "tactics_techniques_admin",
  "vulnerability_library_admin",
  "business_reference_admin",
  "traffic_library_admin",
  "platform_operator",
  "auditor",
] as const;

type PlatformAccessUser = Pick<UserSession, "is_superuser"> & { platform_roles?: string[] | null };

function readRequiredPlatformRoles(user: PlatformAccessUser): string[] {
  if (user.platform_roles === null || user.platform_roles === undefined) {
    throw new Error("CONTRACT_MISSING:user.platform_roles");
  }
  if (!Array.isArray(user.platform_roles)) {
    throw new Error("CONTRACT_INVALID:user.platform_roles");
  }
  return user.platform_roles;
}

function hasAnyPlatformRole(user: PlatformAccessUser | null | undefined, allowedRoles: readonly string[]) {
  if (!user) {
    return false;
  }
  if (user.is_superuser) {
    return true;
  }
  return readRequiredPlatformRoles(user).some((role) => allowedRoles.includes(role));
}

export function canAccessEnvironmentCenter(user: PlatformAccessUser | null | undefined) {
  return hasAnyPlatformRole(user, ENVIRONMENT_PLATFORM_ROLES);
}

export function canAccessIdentityCenter(user: PlatformAccessUser | null | undefined) {
  return hasAnyPlatformRole(user, IDENTITY_PLATFORM_ROLES);
}

export function canAccessAssetCenter(user: PlatformAccessUser | null | undefined) {
  return hasAnyPlatformRole(user, ASSET_PLATFORM_ROLES);
}

export function canAccessExecutionCenter(user: PlatformAccessUser | null | undefined) {
  return hasAnyPlatformRole(user, EXECUTION_PLATFORM_ROLES);
}

export function canAccessIntelligenceCenter(user: PlatformAccessUser | null | undefined) {
  return hasAnyPlatformRole(user, INTELLIGENCE_PLATFORM_ROLES);
}

export function canAccessTacticsTechniquesCenter(user: PlatformAccessUser | null | undefined) {
  return hasAnyPlatformRole(user, TACTICS_TECHNIQUES_PLATFORM_ROLES);
}

export function canAccessCapabilityGraphCenter(user: PlatformAccessUser | null | undefined) {
  return hasAnyPlatformRole(user, CAPABILITY_GRAPH_PLATFORM_ROLES);
}

export function canAccessVulnerabilityLibraryCenter(user: PlatformAccessUser | null | undefined) {
  return hasAnyPlatformRole(user, VULNERABILITY_LIBRARY_PLATFORM_ROLES);
}

export function canAccessBusinessReferenceCenter(user: PlatformAccessUser | null | undefined) {
  return hasAnyPlatformRole(user, BUSINESS_REFERENCE_PLATFORM_ROLES);
}

export function canAccessTrafficLibraryCenter(user: PlatformAccessUser | null | undefined) {
  return hasAnyPlatformRole(user, TRAFFIC_LIBRARY_PLATFORM_ROLES);
}

export function canAccessAirangeAdminPlatform(user: PlatformAccessUser | null | undefined) {
  return hasAnyPlatformRole(user, AIRANGE_ADMIN_PLATFORM_ROLES);
}

export interface LoginPayload {
  username: string;
  password: string;
  captcha_token?: string;
  captcha_code?: string;
  screen_width?: number;
  screen_height?: number;
  viewport_width?: number;
  viewport_height?: number;
  device_pixel_ratio?: number;
  timezone?: string;
  language?: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_expires_in: number;
  user: UserSession;
}

export interface AuthProviderStatus {
  auth_mode: import("./dicts").AuthMode;
  sso_center_base_url: string | null;
  mode?: "local_auth" | "hybrid_identity" | "platform_identity";
  phase?: "transition" | "target";
  dependencies?: string[];
}

export interface PublicSystemConfig {
  platform_name: string;
  candidate_portal_base_url: string | null;
  logo_url: string | null;
  favicon_url: string | null;
  maintenance_mode: boolean;
}

export interface LoginCaptchaChallenge {
  captcha_token: string;
  captcha_svg_data_url: string;
}

export type AuthLoginSource = "local" | "sso";
export type SsoOnlyAuthLoginSource = Extract<AuthLoginSource, "sso">;

export interface ChangePasswordPayload {
  current_password: string;
  new_password: string;
}

export interface UpdateProfilePayload {
  full_name?: string;
  avatar_url?: string | null;
  phone?: string | null;
}
